import {
  CUSTOM_STUDY_TYPE_ID,
  MAX_STUDY_TYPE_SUGGESTION_TEXT_CHARS,
  STUDY_TYPE_SUGGESTION_POLL_MS,
  STUDY_TYPE_SUGGESTION_WAIT_MS,
  type StoredStudyTypeSuggestion,
  type StudyTypeId,
  type StudyTypeSelection,
  studyTypeById,
} from '@sigora/domain';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { suggestStudyType } from '../api/client.ts';
import { Pending } from '../ui/Pending.tsx';

export interface StudyTypeSuggestionProps {
  studyId: string;
  /** What the study read says about the last sentence, or nothing while there has been none. */
  suggestion: StoredStudyTypeSuggestion | null;
  /** What the study has chosen, so the block can say which of its two answers is the current one. */
  selection: StudyTypeSelection | null;
  /** True while the picker is storing a choice; every control here waits for it. */
  busy: boolean;
  /** Stores a choice through the picker, which owns the saving and refusal states. */
  onChoose: (choice: { studyTypeId: StudyTypeId }) => void;
  /** Reads the study again, which is how a pending suggestion is asked about. */
  onStudyChanged: () => void;
}

/**
 * The newer of two answers to "what did the researcher last ask?".
 *
 * The request this screen just made is known before the study read catches up with it, and showing
 * the read's older answer in that beat would offer a type for words the reader has just replaced.
 * Once the read carries the same request it is the one to show, since it is where the answer lands.
 */
const newest = (
  read: StoredStudyTypeSuggestion | null,
  asked: StoredStudyTypeSuggestion | null,
): StoredStudyTypeSuggestion | null => {
  if (asked === null || (read !== null && read.requestId === asked.requestId)) {
    return read;
  }

  return read === null || read.requestedAt < asked.requestedAt ? asked : read;
};

/**
 * "Something else? Say it in a sentence": a box, a Go, and what Sigora makes of the sentence.
 *
 * The answer is offered as "Use X" with X's bring/get line, and "Continue as custom" is offered
 * beside it and after it — never before an answer has arrived, so the suggestion is always what
 * the reader sees first. The Custom this stores carries no area: the reader described their study
 * rather than standing in one, so there is no area to record.
 *
 * The answer is written by the worker, so a pending one is followed by re-reading the study rather
 * than by a route of its own, and that stops the moment the answer settles.
 */
export const StudyTypeSuggestion = ({
  studyId,
  suggestion,
  selection,
  busy,
  onChoose,
  onStudyChanged,
}: StudyTypeSuggestionProps) => {
  const [draft, setDraft] = useState(() => suggestion?.text ?? '');
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [asked, setAsked] = useState<StoredStudyTypeSuggestion | null>(null);
  /** The request that stopped being waited on, so a stale deadline cannot silence a new one. */
  const [waitedOut, setWaitedOut] = useState<string | null>(null);
  const pendingSince = useRef<{ requestId: string; at: number } | null>(null);
  /** Counts the poll's ticks, so each tick arms the next whatever the read it asked for returned. */
  const [tick, setTick] = useState(0);
  /**
   * The last suggestion a read carried. A read that fails hands this `null` — the study could not be
   * read, not a study with no suggestion — and forgetting the request it was waiting on would drop
   * the waiting line and end the poll over one bad read.
   */
  const lastRead = useRef(suggestion);
  if (suggestion !== null) {
    lastRead.current = suggestion;
  }

  const shown = newest(suggestion ?? lastRead.current, asked);
  const pendingId = shown?.status === 'pending' ? shown.requestId : null;

  const ask = (text: string) => {
    setAsking(true);
    setAskError(null);
    void suggestStudyType(studyId, text).then(
      (stored) => {
        setAsking(false);
        setAsked(stored);
        onStudyChanged();
      },
      (failure: unknown) => {
        setAsking(false);
        setAskError(
          failure instanceof Error ? failure.message : 'Sigora could not read that sentence.',
        );
      },
    );
  };

  /**
   * One timer at a time, and none once the answer has settled or the wait has run out.
   *
   * Each tick arms the next through its own counter rather than through what the read brought back.
   * Keyed on the read instead, a run of failed reads re-renders nothing — the shell writes the same
   * empty study each time — so no timer would be armed, the waiting line would never move and the
   * deadline, which is only asked when a timer runs, would never arrive. The deadline is asked both
   * before a timer is armed and when it fires. Torn down with the render that armed it, so nothing is
   * left ticking when the screen moves on.
   */
  useEffect(() => {
    if (pendingId === null || waitedOut === pendingId) {
      return undefined;
    }

    if (pendingSince.current?.requestId !== pendingId) {
      pendingSince.current = { requestId: pendingId, at: Date.now() };
    }

    const since = pendingSince.current.at;
    const overdue = () => Date.now() - since >= STUDY_TYPE_SUGGESTION_WAIT_MS;
    if (overdue()) {
      setWaitedOut(pendingId);
      return undefined;
    }

    const timer = setTimeout(() => {
      if (overdue()) {
        setWaitedOut(pendingId);
        return;
      }

      onStudyChanged();
      setTick((count) => count + 1);
    }, STUDY_TYPE_SUGGESTION_POLL_MS);

    return () => clearTimeout(timer);
  }, [pendingId, waitedOut, onStudyChanged, tick]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (text !== '') {
      ask(text);
    }
  };

  const disabled = busy || asking;
  /**
   * Resolved through the catalogue rather than trusted, so the only "Use X" that can be drawn is one
   * naming a type the catalogue holds; anything else is shown as the unrecognised answer it is.
   */
  const suggested = shown?.status === 'ready' ? studyTypeById(shown.studyTypeId) : undefined;
  const waiting = shown?.status === 'pending' && waitedOut !== shown.requestId;
  const settled = shown !== null && !waiting;

  return (
    <div className="study-type-suggest" data-test-id="study-type-suggest">
      <form onSubmit={submit}>
        <label className="study-type-lede" htmlFor="study-type-suggest-text">
          Something else? Say it in a sentence.
        </label>
        <div className="study-type-suggest-row">
          <input
            id="study-type-suggest-text"
            data-test-id="study-type-suggest-text"
            value={draft}
            maxLength={MAX_STUDY_TYPE_SUGGESTION_TEXT_CHARS}
            placeholder="For example: do people trust our new packaging claim?"
            onChange={(event) => setDraft(event.target.value)}
          />
          <button
            type="submit"
            data-test-id="study-type-suggest-go"
            disabled={disabled || draft.trim() === ''}
          >
            Go
          </button>
        </div>
      </form>

      {askError !== null && (
        <p className="chat-error" data-test-id="study-type-suggest-error" role="alert">
          {askError}
        </p>
      )}

      {shown !== null && (
        <div data-test-id="study-type-suggestion" aria-live="polite">
          {waiting && (
            <Pending live={false} className="study-type-saving" testId="study-type-suggest-pending">
              Sigora is reading “{shown.text}”…
            </Pending>
          )}

          {settled && (
            <p className="study-type-lede" data-test-id="study-type-suggest-answer">
              You said: “{shown.text}”.{' '}
              {suggested !== undefined
                ? `The closest study type is ${suggested.name}.`
                : shown.status === 'pending'
                  ? 'Sigora is taking longer than it should to answer.'
                  : shown.status === 'unavailable'
                    ? 'Sigora could not suggest a study type just now.'
                    : 'Sigora could not match that to one of the study types.'}
            </p>
          )}

          {suggested !== undefined && (
            <button
              type="button"
              className="study-type-card choice"
              data-test-id="study-type-suggest-use"
              aria-pressed={selection?.id === suggested.id}
              disabled={disabled}
              onClick={() => onChoose({ studyTypeId: suggested.id })}
            >
              <span className="study-type-card-name">Use {suggested.name}</span>
              <span className="study-type-card-detail">{suggested.bringGet}</span>
            </button>
          )}

          {settled && suggested === undefined && (
            <button
              type="button"
              data-test-id="study-type-suggest-retry"
              disabled={disabled}
              onClick={() => ask(shown.text)}
            >
              Try again
            </button>
          )}

          {settled && (
            <button
              type="button"
              className="study-type-card choice"
              data-test-id="study-type-suggest-custom"
              aria-pressed={selection?.id === CUSTOM_STUDY_TYPE_ID && selection.category === null}
              disabled={disabled}
              onClick={() => onChoose({ studyTypeId: CUSTOM_STUDY_TYPE_ID })}
            >
              <span className="study-type-card-name">Continue as custom</span>
              <span className="study-type-card-detail">
                Sigora asks what you have and offers general goal options.
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
